import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Pencil, RefreshCw, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const CUSTOM_SENTINEL = '__custom__';

// Small fallback list — used only if the OpenRouter API call fails so the
// picker never becomes unusable. Admins can still type any custom ID.
const FALLBACK_TEXT = [
  {
    provider: 'anthropic',
    label: 'Anthropic Claude',
    items: [
      { id: 'anthropic/claude-opus-4.7', name: 'Opus 4.7' },
      { id: 'anthropic/claude-sonnet-4.6', name: 'Sonnet 4.6' },
    ],
  },
];
const FALLBACK_IMAGE = [
  {
    provider: 'google',
    label: 'Google Gemini',
    items: [
      { id: 'google/gemini-3.1-flash-image-preview', name: 'Gemini 3.1 (影像)' },
    ],
  },
];

/**
 * OpenRouter model picker with LIVE catalogue.
 *
 * Fetches the full OpenRouter model list via GET /api/carousel/admin/openrouter/models
 * (backend-cached 5min) so admins always see the latest models without redeploy.
 * Filters by input modality for `mode='image'` (only shows models that accept image input).
 *
 * Props:
 *   value: string (current model id)
 *   onChange: (newModelId: string) => void
 *   mode: 'text' | 'image'
 *   defaultModel?: string
 *   testIdPrefix?: string
 */
export const OpenRouterModelPicker = ({
  value,
  onChange,
  mode = 'text',
  defaultModel,
  testIdPrefix = 'model',
}) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [refreshTs, setRefreshTs] = useState(0);

  const fetchModels = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await axios.get(`${API}/api/carousel/admin/openrouter/models`, {
        timeout: 20000,
        headers: (() => {
          const t = localStorage.getItem('token') || sessionStorage.getItem('token');
          return t ? { Authorization: `Bearer ${t}` } : {};
        })(),
      });
      const raw = r.data.groups || [];
      // Filter for image-input models when mode='image'
      const filtered = raw
        .map((g) => ({
          ...g,
          items: (g.items || []).filter((it) => {
            if (mode !== 'image') return true;
            // For image mode: only models that accept image OR output image
            const inMods = it.input_modalities || [];
            const outMods = it.output_modalities || [];
            return inMods.includes('image') || outMods.includes('image');
          }),
        }))
        .filter((g) => g.items.length > 0);
      setGroups(filtered);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || '無法連接 OpenRouter');
      setGroups(mode === 'image' ? FALLBACK_IMAGE : FALLBACK_TEXT);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchModels();
  }, [mode, refreshTs]);

  const knownValues = useMemo(
    () => new Set(groups.flatMap((g) => g.items.map((it) => it.id))),
    [groups]
  );

  const isCustomInitial = value && !knownValues.has(value) && !loading;
  const [customMode, setCustomMode] = useState(false);
  const [customDraft, setCustomDraft] = useState('');

  // Sync custom mode when knownValues changes
  useEffect(() => {
    if (isCustomInitial) {
      setCustomMode(true);
      setCustomDraft(value);
    }
  }, [isCustomInitial, value]);

  const handleSelectChange = (picked) => {
    if (picked === CUSTOM_SENTINEL) {
      setCustomMode(true);
      return;
    }
    setCustomMode(false);
    setCustomDraft('');
    onChange(picked);
  };

  const commitCustom = () => {
    const trimmed = customDraft.trim();
    if (trimmed) onChange(trimmed);
  };

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-picker`}>
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <Select
            value={customMode ? CUSTOM_SENTINEL : value || ''}
            onValueChange={handleSelectChange}
            disabled={loading}
          >
            <SelectTrigger
              className="bg-white border-neutral-300 text-sm font-mono"
              data-testid={`${testIdPrefix}-trigger`}
            >
              <SelectValue placeholder={loading ? '載入 OpenRouter 模型清單…' : '選擇一個模型'} />
            </SelectTrigger>
            <SelectContent
              className="max-h-[420px]"
              data-testid={`${testIdPrefix}-content`}
            >
              {groups.length === 0 && !loading && (
                <div className="text-xs text-neutral-500 px-3 py-2">
                  未載入到任何模型，請「重新整理」或選「自訂 Model ID」
                </div>
              )}
              {groups.map((g) => (
                <SelectGroup key={g.provider}>
                  <SelectLabel className="text-[10px] tracking-wider text-neutral-500 uppercase">
                    {g.label} · {g.items.length}
                  </SelectLabel>
                  {g.items.map((it) => (
                    <SelectItem
                      key={it.id}
                      value={it.id}
                      className="text-xs font-mono"
                      data-testid={`${testIdPrefix}-opt-${it.id.replace(/[^a-z0-9]/gi, '-')}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-neutral-800 font-sans font-medium">
                          {it.name}
                        </span>
                        <span className="text-[10px] text-neutral-400 font-mono">
                          {it.id}
                        </span>
                        {it.context_length && (
                          <span className="text-[10px] text-neutral-400 bg-neutral-100 px-1 py-0.5 rounded font-mono">
                            {Math.round(it.context_length / 1000)}K
                          </span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectGroup>
              ))}
              <SelectGroup>
                <SelectLabel className="text-[10px] tracking-wider text-neutral-500 uppercase">
                  其他
                </SelectLabel>
                <SelectItem value={CUSTOM_SENTINEL} className="text-xs">
                  <div className="flex items-center gap-2">
                    <Pencil className="w-3 h-3" />
                    自訂 Model ID…
                  </div>
                </SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
        <button
          type="button"
          onClick={() => setRefreshTs(Date.now())}
          disabled={loading}
          title="重新載入 OpenRouter 模型清單"
          className="h-9 w-9 rounded-md border border-neutral-300 bg-white text-neutral-600 hover:border-primary-500 hover:text-primary-500 flex items-center justify-center transition-colors disabled:opacity-50"
          data-testid={`${testIdPrefix}-refresh`}
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {customMode && (
        <div className="flex gap-2">
          <input
            type="text"
            value={customDraft}
            onChange={(e) => setCustomDraft(e.target.value)}
            onBlur={commitCustom}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitCustom();
              }
            }}
            placeholder="provider/model-id"
            className="flex-1 bg-white border border-neutral-300 focus:border-primary-500 rounded-md px-3 py-2 text-sm font-mono outline-none"
            data-testid={`${testIdPrefix}-custom-input`}
          />
          <button
            type="button"
            onClick={commitCustom}
            className="text-xs font-medium text-white bg-primary-500 hover:bg-primary-600 px-3 py-2 rounded-md"
            data-testid={`${testIdPrefix}-custom-confirm`}
          >
            確認
          </button>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-1.5 text-[11px] text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-1">
          <AlertCircle className="w-3 h-3" />
          <span>載入 OpenRouter 清單失敗：{error}。已改用備用清單，仍可以自訂 Model ID。</span>
        </div>
      )}

      <div className="flex items-center justify-between text-[11px] gap-3 flex-wrap">
        <span className="text-neutral-600">
          目前使用：
          <code
            className="ml-1 text-primary-600 font-semibold font-mono"
            data-testid={`${testIdPrefix}-current`}
          >
            {value || '(未設定)'}
          </code>
        </span>
        {defaultModel && defaultModel !== value && (
          <span className="text-neutral-400">工廠預設：{defaultModel}</span>
        )}
      </div>
    </div>
  );
};

export default OpenRouterModelPicker;
